import { useState } from 'react';

import { useMe } from '@crehana/auth';
import { PageLoader } from '@crehana/compass.ui/v2';
import { useUITheme } from '@crehana/web';
import {
  useAvailableSurvey,
  useReminderEnabled,
} from '@crehana/web/components/Survey/hooks';
import SurveyReminder from '@crehana/web/components/SurveyReminder';

import AdmindashboardView from '@/shared/components/AdmindashboardView';
import SnackBarMessage from '@/views/AdminDashboard/components/SnackBarMessage';
import useConfiguration from '@/views/AdminDashboard/hooks/useConfiguration';
import useRoutes from '@/views/AdminDashboard/hooks/useRoutes';
import useSnackbarMessage from '@/views/AdminDashboard/hooks/useSnackbarMessage';

import { TourProvider } from '../TourContext/TourContext';
import { AdmindashboardContext } from './AdmindashboardContext';
import CrossPlatformLayout from './components/CrossPlatformLayout';
import NotificationDialog, {
  type NotificationDialogProps,
} from './components/NotificationDialog';
import SidebarMenu from './components/SidebarMenu';
import useCurrentUser from './hooks/useCurrentUser';
import { useGetAllowedModulesBySlug } from './hooks/useGetAllowedModulesBySlug/useGetAllowedModulesBySlug';
import type { AdminDashboardContextValues } from './types';

type IAdminDashboardProviderProps = React.PropsWithChildren<{
  hideLayout?: boolean;
}>;

const AdmindashboardInternalProvider: React.FC<
  IAdminDashboardProviderProps
> = ({ children, hideLayout }) => {
  const { isDark } = useUITheme();
  const { me, loading: isLoadingMe } = useMe();

  const [panelUrl, setPanelUrl] = useState<string | undefined>();

  const { openSnackbar, isOpen, message, state } = useSnackbarMessage();
  const [notificationDialogState, setNotificationDialogState] =
    useState<NotificationDialogProps | null>(null);
  const { hasAvailableSurveyToAnswer, surveyToAnswerId, loading } =
    useAvailableSurvey();
  const {
    canUseInduction,
    canUseIntegrations,
    configuration,
    data,
    hasProductDemoTest,
    isCorporation,
    isLoadingConfig,
    logoThumbnail,
    name,
    originalId,
    slug,
  } = useConfiguration();

  const {
    adminModules,
    hasAdminModules,
    isNonCentralizedUser,
    loading: loadingAllowedModules,
  } = useGetAllowedModulesBySlug({
    organizationSlug: slug,
    email: me?.email,
  });

  const { currentUser, isLoadingCurrentUser } = useCurrentUser();

  const {
    isBrazil,
    isTalent,
    mainRoutes,
    secondaryRoutes,
    isLoadingRoutes,
    isCrossPlatformActive,
    upperRoutes,
  } = useRoutes({
    canUseInduction,
    canUseIntegrations,
    isCorporation,
    hasAdminModules,
  });

  const npsReminderIsEnabledToShow = useReminderEnabled({
    hasAvailableSurveyToAnswer: hasAvailableSurveyToAnswer,
    surveyToAnswerId: Boolean(surveyToAnswerId),
    skip: loading,
    userName: me?.username!,
  });

  const isLoading =
    isLoadingConfig ||
    isLoadingRoutes ||
    isLoadingCurrentUser ||
    !data ||
    loadingAllowedModules ||
    isLoadingMe;

  const value: AdminDashboardContextValues = {
    currentUserOrganization: currentUser,
    hasGamificationAvailable: configuration?.hasGamificationAvailable || false,
    hasProductDemoTest,
    hasRequestedDemoElevate: configuration?.hasRequestedDemoElevate || false,
    hasSeenWellcomeToElevate: configuration?.hasSeenWellcomeToElevate || false,
    hasSkillXpEvolutionAvailable:
      configuration?.hasSkillXpEvolutionAvailable || false,
    hasStudioAvailable: configuration?.hasStudioAvailable || false,
    isBrazil,
    isCorporation,
    isTalent,
    name,
    openSnackbar,
    organizationId: originalId,
    slug,
    organizationLogo: {
      light: {
        expanded: logoThumbnail,
        collapsed: configuration?.isotype,
      },
      dark: {
        expanded: configuration?.darkModeLogo,
        collapsed: configuration?.darkModeIsotype,
      },
    },
    setNotificationDialogState,
    closeNotificationDialog: () => {
      setNotificationDialogState(null);
    },
    panelUrl,
    setPanelUrl,
    adminModules,
    hasAdminModules,
    isNonCentralizedUser,
    isLoading,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full min-h-screen bg-base-lighter-8">
        <PageLoader size="s" />
      </div>
    );
  }

  if (hideLayout) {
    return (
      <AdmindashboardContext.Provider value={value}>
        <TourProvider>
          {children}
          <SnackBarMessage
            isDark={isDark}
            isOpen={isOpen}
            message={message}
            state={state}
          />
          {notificationDialogState && (
            <NotificationDialog {...notificationDialogState} />
          )}
        </TourProvider>
      </AdmindashboardContext.Provider>
    );
  }

  return (
    <AdmindashboardContext.Provider value={value}>
      <TourProvider>
        {isCrossPlatformActive ? (
          <CrossPlatformLayout slug={slug}>
            {children}
            {npsReminderIsEnabledToShow && (
              <SurveyReminder surveyId={surveyToAnswerId || ''} />
            )}
          </CrossPlatformLayout>
        ) : (
          <SidebarMenu
            slug={slug}
            isDark={isDark}
            routes={mainRoutes}
            secondaryRoutes={secondaryRoutes}
            baseUpperRoutes={upperRoutes}
            hasAdminModules={hasAdminModules}
          >
            <div
              className="flex flex-col w-full h-screen overflow-y-auto"
              id="AdminDashboardBody"
            >
              {children}
              {npsReminderIsEnabledToShow && (
                <SurveyReminder surveyId={surveyToAnswerId || ''} />
              )}
            </div>
          </SidebarMenu>
        )}
        <SnackBarMessage
          isDark={isDark}
          isOpen={isOpen}
          message={message}
          state={state}
        />
        {notificationDialogState && (
          <NotificationDialog {...notificationDialogState} />
        )}
      </TourProvider>
    </AdmindashboardContext.Provider>
  );
};

function withProviders(Component: React.FC<IAdminDashboardProviderProps>) {
  const Providers = (props: IAdminDashboardProviderProps) => {
    const { slug, originalId, isLoadingConfig } = useConfiguration();

    if (isLoadingConfig) {
      return (
        <div className="flex items-center justify-center w-full min-h-screen bg-base-lighter-8">
          <PageLoader size="s" />
        </div>
      );
    }

    return (
      <AdmindashboardView slug={slug} organizationId={originalId}>
        <Component {...props} />
      </AdmindashboardView>
    );
  };

  Providers.displayName = `AdmindashboardProvider`;

  return Providers;
}

export const AdmindashboardProvider = withProviders(
  AdmindashboardInternalProvider,
);
